import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  topCanciones,
  cancionesSolicitadas,
  scoreAcciones,
  reglasCanciones,
  globalStats,
  estadoLabels,
} from '@/mocks/cancionesSolicitadas';
import type { EstadoCancion } from '@/mocks/cancionesSolicitadas';

type TabCanciones = 'mas-pedidas' | 'ultimas-pedidas' | 'top-canciones' | 'acciones' | 'reglas';

export default function CancionesSolicitadasSection() {
  const [activeTab, setActiveTab] = useState<TabCanciones>('mas-pedidas');
  const [estadoFiltro, setEstadoFiltro] = useState<EstadoCancion | 'todos'>('todos');

  const filtradas = cancionesSolicitadas.filter((c) =>
    estadoFiltro === 'todos' ? true : c.estado === estadoFiltro
  );

  const masPedidas = [...cancionesSolicitadas].sort((a, b) => b.score - a.score).slice(0, 6);
  const ultimasPedidas = [...cancionesSolicitadas].sort(
    (a, b) => new Date(b.fechaSolicitud).getTime() - new Date(a.fechaSolicitud).getTime()
  ).slice(0, 6);

  const estadoPills: { estado: EstadoCancion | 'todos'; label: string }[] = [
    { estado: 'todos', label: 'Todas' },
    { estado: 'pendiente', label: 'Pendientes' },
    { estado: 'aceptada', label: 'Aceptadas' },
    { estado: 'al_aire', label: 'Al Aire' },
    { estado: 'rechazada', label: 'Rechazadas' },
  ];

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-[1100px] mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-100 text-rose-700 text-[11px] font-bold mb-4">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
            CANCIONES SOLICITADAS
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            Pedí Tu Canción, Dedicá y Participá
          </h2>
          <p className="text-foreground-500 text-sm max-w-[620px] mx-auto leading-relaxed">
            Solicitá canciones con mensaje y dedicatoria. Seguí el estado en tiempo real: Pendiente, Aceptada, Al Aire. Las más pedidas suben en el ranking. Todo dentro de Cabina.
          </p>
        </div>

        {/* Stats mini */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'Pedidas Hoy', valor: globalStats.cancionesSolicitadasHoy.toString(), icono: 'ri-music-line', color: 'text-rose-600', bg: 'bg-rose-100' },
            { label: 'Al Aire Hoy', valor: globalStats.cancionesAlAireHoy.toString(), icono: 'ri-broadcast-line', color: 'text-emerald-600', bg: 'bg-emerald-100' },
            { label: 'Pendientes', valor: globalStats.cancionesPendientes.toString(), icono: 'ri-time-line', color: 'text-amber-600', bg: 'bg-amber-100' },
            { label: 'Score Generado', valor: globalStats.scoreTotalGenerado.toLocaleString(), icono: 'ri-flashlight-line', color: 'text-violet-600', bg: 'bg-violet-100' },
          ].map((stat) => (
            <div key={stat.label} className="bg-background-50 rounded-2xl border border-background-200 p-4 text-center">
              <div className={`w-9 h-9 rounded-xl ${stat.bg} flex items-center justify-center mx-auto mb-2`}>
                <i className={`${stat.icono} ${stat.color} text-sm`}></i>
              </div>
              <p className="text-foreground-950 font-bold text-lg">{stat.valor}</p>
              <p className="text-foreground-400 text-[10px]">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center gap-1.5 mb-6 flex-wrap">
          {([
            { id: 'mas-pedidas', icono: 'ri-fire-line', label: 'Más Pedidas' },
            { id: 'ultimas-pedidas', icono: 'ri-time-line', label: 'Últimas Pedidas' },
            { id: 'top-canciones', icono: 'ri-trophy-line', label: 'Top Canciones' },
            { id: 'acciones', icono: 'ri-flashlight-line', label: 'Score' },
            { id: 'reglas', icono: 'ri-scales-line', label: 'Reglas' },
          ] as { id: TabCanciones; icono: string; label: string }[]).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-foreground-950 text-background-50'
                  : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-200'
              }`}
            >
              <i className={tab.icono}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'top-canciones' ? (
          /* Top Canciones */
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {topCanciones.slice(0, 10).map((top) => (
              <div key={top.posicion} className="bg-background-50 rounded-2xl border border-background-200 p-4 text-center hover:border-background-300 transition-colors group cursor-pointer">
                <div className="relative mb-3">
                  <img
                    src={top.portada}
                    alt={top.cancion}
                    className="w-full aspect-square rounded-xl object-cover"
                    loading="lazy"
                  />
                  <span className="absolute -top-2 -left-2 w-7 h-7 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 border-2 border-background-50 flex items-center justify-center text-[10px] font-bold text-background-50 shadow-sm">
                    {top.posicion}
                  </span>
                </div>
                <p className="text-foreground-950 font-bold text-xs leading-tight mb-0.5">{top.cancion}</p>
                <p className="text-foreground-500 text-[10px]">{top.artista}</p>
                <div className="flex items-center justify-center gap-3 mt-2">
                  <span className="text-foreground-400 text-[9px]">{top.solicitudes} pedidas</span>
                  <span className="text-foreground-400 text-[9px]">{top.vecesAlAire}x al aire</span>
                </div>
              </div>
            ))}
          </div>
        ) : activeTab === 'acciones' ? (
          /* Score Acciones */
          <div className="max-w-[700px] mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {scoreAcciones.map((acc) => (
                <div key={acc.id} className="bg-background-50 rounded-2xl border border-background-200 p-5 text-center">
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${acc.color} flex items-center justify-center mx-auto mb-3`}>
                    <i className={`${acc.icono} text-background-50 text-lg`}></i>
                  </div>
                  <p className="text-foreground-950 font-bold text-sm mb-1">{acc.accion}</p>
                  <p className="text-foreground-400 text-xs leading-relaxed mb-3">{acc.descripcion}</p>
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 text-xs font-bold">
                    <i className="ri-add-line"></i>+{acc.puntos} pts
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : activeTab === 'reglas' ? (
          /* Reglas */
          <div className="max-w-[900px] mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {reglasCanciones.map((regla) => (
                <div key={regla.id} className="bg-background-50 rounded-2xl border border-background-200 p-5">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${regla.color} flex items-center justify-center shrink-0`}>
                      <i className={`${regla.icono} text-background-50 text-sm`}></i>
                    </div>
                    <div>
                      <h4 className="text-foreground-950 font-bold text-sm mb-1">{regla.titulo}</h4>
                      <p className="text-foreground-500 text-xs leading-relaxed">{regla.descripcion}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Más Pedidas o Últimas Pedidas */
          <div>
            {/* Estado filter pills */}
            <div className="flex items-center justify-center gap-1.5 mb-5 flex-wrap">
              {estadoPills.map((pill) => (
                <button
                  key={pill.estado}
                  onClick={() => setEstadoFiltro(pill.estado)}
                  className={`px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    estadoFiltro === pill.estado
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-100 text-foreground-500 border border-background-200 hover:border-background-300'
                  }`}
                >
                  {pill.label}
                </button>
              ))}
            </div>

            {/* Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(activeTab === 'mas-pedidas' ? masPedidas : ultimasPedidas)
                .filter((c) => estadoFiltro === 'todos' || c.estado === estadoFiltro)
                .map((req) => {
                  const estado = estadoLabels[req.estado];
                  return (
                    <div
                      key={req.id}
                      className={`bg-background-50 rounded-2xl border border-background-200 p-4 hover:border-background-300 transition-colors ${
                        req.estado === 'rechazada' ? 'opacity-60' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <img
                          src={req.portada}
                          alt={req.cancion}
                          className="w-12 h-12 rounded-xl object-cover shrink-0"
                          loading="lazy"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className="text-foreground-950 font-bold text-xs truncate">{req.cancion}</p>
                            <span className={`w-1.5 h-1.5 rounded-full ${estado.dot} shrink-0`}></span>
                          </div>
                          <p className="text-foreground-500 text-[10px]">{req.artista}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded-md ${estado.bg} ${estado.text} text-[9px] font-semibold shrink-0 flex items-center gap-1`}>
                          <i className={`${estado.icono} text-[9px]`}></i>
                          {estado.label}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 mb-2">
                        <img
                          src={req.solicitanteAvatar}
                          alt={req.solicitante}
                          className="w-5 h-5 rounded-full object-cover"
                          loading="lazy"
                        />
                        <span className="text-foreground-600 text-[10px]">{req.solicitante}</span>
                        <span className="text-foreground-400 text-[9px]">{req.solicitanteCiudad}</span>
                      </div>

                      <p className="text-foreground-500 text-[11px] leading-relaxed line-clamp-2 mb-2">
                        &ldquo;{req.mensaje}&rdquo;
                      </p>

                      {req.dedicatoria && (
                        <p className="text-accent-700 text-[10px] flex items-center gap-1 mb-2">
                          <i className="ri-heart-line"></i>
                          {req.dedicatoria}
                        </p>
                      )}

                      {req.motivoRechazo && (
                        <p className="text-rose-600 text-[10px] flex items-center gap-1 bg-rose-50 rounded-lg px-2 py-1">
                          <i className="ri-error-warning-line"></i>
                          {req.motivoRechazo}
                        </p>
                      )}

                      {req.estado !== 'rechazada' && (
                        <div className="flex items-center gap-3 mt-2 pt-2 border-t border-background-100">
                          <span className="text-foreground-400 text-[9px] flex items-center gap-1">
                            <i className="ri-share-forward-line"></i>{req.compartidos}
                          </span>
                          <span className="text-foreground-400 text-[9px] flex items-center gap-1">
                            <i className="ri-group-line"></i>{req.participantes}
                          </span>
                          <span className="text-emerald-700 text-[9px] font-semibold ml-auto flex items-center gap-0.5">
                            <i className="ri-flashlight-fill"></i>{req.score} pts
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>

            {filtradas.length === 0 && (
              <div className="text-center py-10">
                <p className="text-foreground-400 text-sm">No hay canciones en este estado.</p>
              </div>
            )}
          </div>
        )}

        {/* CTA */}
        <div className="mt-10 text-center">
          <Link
            to="/canciones-solicitadas"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-rose-500 to-amber-500 text-background-50 font-bold text-sm cursor-pointer hover:from-rose-600 hover:to-amber-600 transition-all whitespace-nowrap"
          >
            <i className="ri-music-line"></i>
            Ver Todas las Canciones Solicitadas
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Regla final */}
        <div className="mt-8 text-center">
          <p className="text-foreground-400 text-xs italic">
            &ldquo;Todo dentro de Cabina. Pedir, compartir y participar: la música la elige la comunidad.&rdquo;
          </p>
        </div>
      </div>
    </section>
  );
}